/**
 * WeaveIn.ai Platform - Medicaid Enrollment API Service
 * TypeScript service layer for Medicaid enrollment management
 */

import axios, { AxiosInstance } from 'axios';
import { API_VERSION } from './organizations';
import { initializeActiveCboId } from '../../utils/auth';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type EnrollmentStatus =
  | 'INITIAL_APPLICATION'
  | 'PENDING_VERIFICATION'
  | 'ACTIVELY_ENROLLED'
  | 'PENDING_REDETERMINATION'
  | 'SUSPENDED'
  | 'TERMINATED'
  | 'REACTIVATED'
  | 'COMPLETED';

export type WorkRequirementStatus =
  | 'EXEMPT'
  | 'REQUIRED'
  | 'COMPLIANT'
  | 'NON_COMPLIANT'
  | 'PENDING_VERIFICATION';

export interface MemberInfo {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  ssn_last_4?: string;
  phone_number?: string;
  email?: string;
  preferred_language?: string;
}

export interface AddressInfo {
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  county?: string;
}

export interface HouseholdInfo {
  household_size: number;
  number_of_dependents?: number;
}

export interface FinancialInfo {
  monthly_income: number;
  income_sources: string[];
  total_assets?: number;
}

export interface WorkRequirement {
  is_exempt: boolean;
  exemption_reason?: string;
  hours_required?: number;
  qualifying_activities?: string[];
}

// API Response format (flat structure from backend)
export interface MedicaidEnrollmentAPIResponse {
  enrollment_id: number;
  enrollment_number: string;
  member_id?: number;
  member_name?: string;
  status: EnrollmentStatus;
  state: string;
  application_date?: string;
  effective_date?: string;
  work_requirement_status?: WorkRequirementStatus | null;
}

// Full enrollment interface (for detail views with nested data)
export interface MedicaidEnrollment {
  id: number;
  enrollment_id?: number; // Alias for id from API
  enrollment_number: string;
  member_id?: number;
  member_name?: string;
  referral_id?: number;
  program_id?: number;
  organization_id?: number;
  state_code?: string;
  state?: string; // Alias from API
  status: EnrollmentStatus;
  work_requirement_status?: WorkRequirementStatus | null;
  // Nested objects (optional - may not be in list response)
  member_info?: MemberInfo;
  address?: AddressInfo;
  household?: HouseholdInfo;
  financial?: FinancialInfo;
  work_requirement?: WorkRequirement;
  // Dates
  application_date?: string;
  approval_date?: string;
  effective_date?: string;
  termination_date?: string;
  last_redetermination_date?: string;
  next_redetermination_date?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
}

export interface MedicaidEnrollmentCreate {
  member_id?: number;
  referral_id?: number;
  program_id?: number;
  organization_id?: number;
  state_code: string;
  member_info: MemberInfo;
  address: AddressInfo;
  household: HouseholdInfo;
  financial: FinancialInfo;
  work_requirement?: WorkRequirement;
  notes?: string;
}

export interface MedicaidEnrollmentUpdate {
  status?: EnrollmentStatus;
  work_requirement_status?: WorkRequirementStatus;
  member_info?: Partial<MemberInfo>;
  address?: Partial<AddressInfo>;
  household?: Partial<HouseholdInfo>;
  financial?: Partial<FinancialInfo>;
  work_requirement?: Partial<WorkRequirement>;
  approval_date?: string;
  termination_date?: string;
  next_redetermination_date?: string;
  notes?: string;
}

export interface EnrollmentListParams {
  member_id?: number;
  referral_id?: number;
  status?: EnrollmentStatus;
  state_code?: string;
  organization_id?: number;
  skip?: number;
  limit?: number;
}

// ============================================================================
// API CLIENT
// ============================================================================

class MedicaidEnrollmentAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = '') {
    // Initialize active CBO ID from JWT token if not already set
    initializeActiveCboId();

    // Use empty baseURL in production to leverage Vercel's proxy rules
    // This routes requests through Vercel proxy (/api/medicaid-enrollment -> weavein-api.onrender.com)
    // In development, you can pass productConfig.weaveinApi.baseUrl to bypass proxy
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Version': API_VERSION,
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      return config;
    });
  }

  /**
   * Get enrollments with optional filters
   * Transforms API response to match MedicaidEnrollment interface
   */
  async getEnrollments(params?: EnrollmentListParams): Promise<MedicaidEnrollment[]> {
    const response = await this.client.get<MedicaidEnrollmentAPIResponse[]>('/api/medicaid-enrollment/applications', { params });
    // Transform API response to MedicaidEnrollment format
    return response.data.map((item) => ({
      id: item.enrollment_id,
      enrollment_id: item.enrollment_id,
      enrollment_number: item.enrollment_number,
      member_id: item.member_id,
      member_name: item.member_name,
      status: item.status,
      state_code: item.state,
      state: item.state,
      application_date: item.application_date,
      effective_date: item.effective_date,
      work_requirement_status: item.work_requirement_status,
    }));
  }

  /**
   * Get enrollments for a specific member
   */
  async getMemberEnrollments(memberId: number): Promise<MedicaidEnrollment[]> {
    return this.getEnrollments({ member_id: memberId });
  }

  /**
   * Get a single enrollment by ID
   */
  async getEnrollment(enrollmentId: number): Promise<MedicaidEnrollment> {
    const response = await this.client.get(`/api/medicaid-enrollment/applications/${enrollmentId}`);
    return response.data;
  }

  /**
   * Create a new enrollment
   */
  async createEnrollment(data: MedicaidEnrollmentCreate): Promise<MedicaidEnrollment> {
    const response = await this.client.post('/api/medicaid-enrollment/applications', data);
    return response.data;
  }

  /**
   * Update an existing enrollment
   */
  async updateEnrollment(
    enrollmentId: number,
    data: MedicaidEnrollmentUpdate
  ): Promise<MedicaidEnrollment> {
    const response = await this.client.put(`/api/medicaid-enrollment/applications/${enrollmentId}`, data);
    return response.data;
  }

  /**
   * Update enrollment status
   */
  async updateStatus(enrollmentId: number, status: EnrollmentStatus): Promise<MedicaidEnrollment> {
    const response = await this.client.put(
      `/api/medicaid-enrollment/applications/${enrollmentId}/status`,
      { status }
    );
    return response.data;
  }

  /**
   * Delete an enrollment
   */
  async deleteEnrollment(enrollmentId: number): Promise<void> {
    await this.client.delete(`/api/medicaid-enrollment/applications/${enrollmentId}`);
  }

  /**
   * Link a member to an enrollment
   */
  async linkMember(enrollmentId: number, memberId: number): Promise<MedicaidEnrollment> {
    const response = await this.client.post(
      `/api/medicaid-enrollment/applications/${enrollmentId}/members/${memberId}`
    );
    return response.data;
  }

  /**
   * Check eligibility for Medicaid
   */
  async checkEligibility(data: {
    state_code: string;
    household_size: number;
    monthly_income: number;
    total_assets?: number;
  }): Promise<{
    eligible: boolean;
    eligibility_percentage: number;
    income_limit: number;
    asset_limit: number;
    reasons: string[];
  }> {
    const response = await this.client.post('/api/medicaid-enrollment/eligibility-check', data);
    return response.data;
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const medicaidEnrollmentAPI = new MedicaidEnrollmentAPI();

// ============================================================================
// STATUS HELPERS
// ============================================================================

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  INITIAL_APPLICATION: 'Initial Application',
  PENDING_VERIFICATION: 'Pending Verification',
  ACTIVELY_ENROLLED: 'Actively Enrolled',
  PENDING_REDETERMINATION: 'Pending Re-determination',
  SUSPENDED: 'Suspended',
  TERMINATED: 'Terminated',
  REACTIVATED: 'Reactivated',
  COMPLETED: 'Completed',
};

export const ENROLLMENT_STATUS_COLORS: Record<EnrollmentStatus, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  INITIAL_APPLICATION: 'info',
  PENDING_VERIFICATION: 'warning',
  ACTIVELY_ENROLLED: 'success',
  PENDING_REDETERMINATION: 'warning',
  SUSPENDED: 'error',
  TERMINATED: 'error',
  REACTIVATED: 'success',
  COMPLETED: 'default',
};

export const WORK_REQUIREMENT_STATUS_LABELS: Record<WorkRequirementStatus, string> = {
  EXEMPT: 'Exempt',
  REQUIRED: 'Required',
  COMPLIANT: 'Compliant',
  NON_COMPLIANT: 'Non-Compliant',
  PENDING_VERIFICATION: 'Pending Verification',
};

export const WORK_REQUIREMENT_STATUS_COLORS: Record<WorkRequirementStatus, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  EXEMPT: 'info',
  REQUIRED: 'default',
  COMPLIANT: 'success',
  NON_COMPLIANT: 'error',
  PENDING_VERIFICATION: 'warning',
};

/**
 * Format enrollment number for display
 */
export function formatEnrollmentNumber(enrollmentNumber: string): string {
  // e.g., "CA-2025-001234" -> "CA-2025-001234"
  return enrollmentNumber;
}

/**
 * Get days until next re-determination
 */
export function getDaysUntilRedetermination(nextDate?: string): number | null {
  if (!nextDate) return null;
  const now = new Date();
  const next = new Date(nextDate);
  const diff = next.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Check if re-determination is due soon (within 30 days)
 */
export function isRedeterminationDueSoon(nextDate?: string): boolean {
  const days = getDaysUntilRedetermination(nextDate);
  return days !== null && days <= 30 && days >= 0;
}

/**
 * Check if re-determination is overdue
 */
export function isRedeterminationOverdue(nextDate?: string): boolean {
  const days = getDaysUntilRedetermination(nextDate);
  return days !== null && days < 0;
}
